import React from "react";
import { ChevronDown, Download, FileSpreadsheet, FileText } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  BillingPaymentMethod,
  BillingTransaction,
  BillingTransactionsListResponse,
  BillingTransactionGuestSummary,
  BillingTransactionStatus,
  BillingTransactionType,
} from "@/types/billing-transactions";
import { useHotel } from "@/hooks/useHotel";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { Column, DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import BillingTransactionsFilter from "./components/BillingTransactionsFilter";
import { BillingTransactionsStats } from "./components/BillingTransactionsStats";
import { useBillingTransactionsFiltering } from "./hooks/useBillingTransactionsFiltering";
import { BillingTransactionStatusBadge } from "./components/BillingTransactionStatusBadge";
import { BillingTransactionStatusSelect } from "./components/BillingTransactionStatusSelect";
import {
  useBillingTransactionStats,
  useUpdateTransactionStatus,
} from "@/hooks/useBillingTransactions";
import { billingTransactionService } from "@/services/billing-transactions-service";
import { useToast } from "@/components/ui/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  downloadBillingTransactionsCsv,
  downloadBillingTransactionsExcel,
  getBillingTransactionGuestSummary,
  mapBillingTransactionsForExport,
} from "./utils/exportBillingTransactions";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

const getTransactionTypeLabel = (
  type: BillingTransactionType,
  t: (key: string) => string,
) => {
  if (type === BillingTransactionType.REFUND) {
    return t("billing.refund");
  }

  if (type === BillingTransactionType.PARTIAL_REFUND) {
    return t("billing.partialRefund");
  }

  return t("billing.payment");
};

const getPaymentMethodLabel = (
  method: BillingPaymentMethod | undefined,
  t: (key: string) => string,
) => {
  if (method === BillingPaymentMethod.OFFLINE) {
    return t("billing.paymentMethodOffline");
  }

  return t("billing.paymentMethodOnline");
};

const getPaymentSourceLabel = (
  transaction: BillingTransaction,
  t: (key: string) => string,
) => {
  const paymentSource = transaction.metadata?.paymentSource;

  if (paymentSource === "GUEST_WALLET") {
    return t("billing.walletSourceBalance");
  }

  if (paymentSource === "STRIPE_REMAINDER") {
    return t("billing.walletSourceStripeRemainderLabel");
  }

  if (
    transaction.type === BillingTransactionType.PAYMENT &&
    transaction.paymentMethod === BillingPaymentMethod.OFFLINE
  ) {
    return t("billing.walletSourceOfflineCash");
  }

  return undefined;
};

export const BillingTransactionsPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isLoading: isUserLoading } = useAuthenticatedUser();
  const { capabilities } = useAccessCapabilities();
  const canManageBilling = capabilities.canManageBilling;

  const { hotelId: currentHotelId } = useHotel();

  const {
    billingTransactions,
    isLoading,
    error,
    filters,
    actions,
    computed,
    pagination,
    appliedFilters,
    statsFilters,
  } = useBillingTransactionsFiltering(currentHotelId);

  const { mutate } = useUpdateTransactionStatus();
  const [isExporting, setIsExporting] = React.useState(false);
  const statsQuery = useBillingTransactionStats(statsFilters, !isUserLoading);

  const fetchAllFilteredTransactions = async () => {
    const firstPage = (await billingTransactionService.getBillingTransactions(
      appliedFilters,
      1,
      computed.limit,
    )) as BillingTransactionsListResponse;

    const allTransactions = [...firstPage.transactions];

    if (firstPage.totalPages <= 1) {
      return allTransactions;
    }

    const remainingPages = Array.from(
      { length: firstPage.totalPages - 1 },
      (_, index) => index + 2,
    );

    const pageResponses = await Promise.all(
      remainingPages.map((page) =>
        billingTransactionService.getBillingTransactions(
          appliedFilters,
          page,
          computed.limit,
        ),
      ),
    );

    pageResponses.forEach((response) => {
      allTransactions.push(...response.transactions);
    });

    return allTransactions;
  };

  const handleExport = async (formatType: "csv" | "excel") => {
    if (!canManageBilling) {
      return;
    }

    try {
      setIsExporting(true);

      const transactions = await fetchAllFilteredTransactions();

      if (transactions.length === 0) {
        toast({
          title: t("billing.exportNoDataTitle"),
          description: t("billing.exportNoDataDescription"),
          variant: "destructive",
        });
        return;
      }

      const rows = mapBillingTransactionsForExport(transactions);
      const timestamp = format(new Date(), "yyyy-MM-dd-HH-mm");

      if (formatType === "csv") {
        downloadBillingTransactionsCsv(
          rows,
          `guest-transactions-${timestamp}.csv`,
        );
      } else {
        downloadBillingTransactionsExcel(
          rows,
          `guest-transactions-${timestamp}.xls`,
        );
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t("billing.exportFailedDescription");

      toast({
        title: t("billing.exportFailedTitle"),
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleStatusChange = (
    transactionId: string,
    newStatus: BillingTransactionStatus,
  ) => {
    if (!canManageBilling) {
      return;
    }

    mutate({ id: transactionId, newStatus });
  };

  const handleTransactionClick = (
    transaction: BillingTransaction,
    _index: number,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => {
    const target = event.target as HTMLElement | null;
    const interactiveElement = target?.closest(
      'a, button, input, select, textarea, [role="button"], [role="menuitem"]',
    );

    if (interactiveElement) {
      return;
    }

    const bookingId = transaction.bookingId ?? transaction.booking?.id;

    if (!bookingId) {
      toast({
        title: t("billing.reservationRedirectUnavailableTitle"),
        description: t("billing.reservationRedirectUnavailableDescription"),
        variant: "destructive",
      });
      return;
    }

    navigate(`/reservations/${bookingId}?tab=payments`);
  };

  const getGuestDisplayName = (
    guest?: BillingTransactionGuestSummary | null,
  ) => {
    if (!guest) return "";
    if (guest.displayName) return guest.displayName;

    const parts = [guest.firstName, guest.lastName].filter(Boolean);
    return parts.join(" ");
  };

  const getServiceLabels = (transaction: BillingTransaction) => {
    const labels =
      transaction.billingItems
        ?.filter((item) => item.type === "SERVICE")
        .map((item) => {
          const serviceName =
            item.service &&
            typeof item.service === "object" &&
            "name" in item.service &&
            typeof item.service.name === "string"
              ? item.service.name
              : null;

          return serviceName ?? item.description ?? null;
        })
        .filter((label): label is string => Boolean(label)) ?? [];

    return [...new Set(labels)];
  };

  const columns: Column<BillingTransaction>[] = [
    {
      key: "status",
      header: t("billing.status"),
      className: "text-center",
      sortable: true,
      render: (value: BillingTransactionStatus) => (
        <BillingTransactionStatusBadge status={value} />
      ),
    },
    {
      key: "type",
      header: t("billing.type"),
      className: "text-center",
      sortable: true,
      render: (value: BillingTransactionType) => (
        <Badge variant="new_secondary">
          {getTransactionTypeLabel(value, t)}
        </Badge>
      ),
    },
    {
      key: "paymentMethod",
      header: t("billing.paymentMethod"),
      className: "text-center",
      sortable: true,
      render: (value: BillingPaymentMethod | undefined, row) => {
        const sourceLabel = getPaymentSourceLabel(row, t);

        return (
          <div className="space-y-1">
            <Badge variant="new_secondary">
              {getPaymentMethodLabel(value, t)}
            </Badge>
            {sourceLabel ? (
              <div className="text-xs text-zinc-500">{sourceLabel}</div>
            ) : null}
          </div>
        );
      },
    },

    {
      key: "amount",
      header: t("billing.amount"),
      className: "text-right",
      sortable: true,
      render: (value: number, row) => (
        <span className="font-medium">
          {(value / 100).toFixed(2)} {row.currency}
        </span>
      ),
    },
    {
      key: "services",
      header: t("billing.services"),
      className: "text-center",
      render: (_, row) => {
        const services = getServiceLabels(row);

        if (!services.length) {
          return "—";
        }

        return (
          <div className="space-y-1 text-left">
            {services.map((service) => (
              <div key={service} className="text-sm text-zinc-900">
                {service}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "guestId",
      header: t("billing.guest"),
      className: "text-center",
      sortable: true,
      render: (_, row) => {
        const guestSummary = getBillingTransactionGuestSummary(row);
        const guestName = getGuestDisplayName(guestSummary);
        const guestId = row.booking?.guestId;

        if (!guestSummary && !guestId) {
          return "—";
        }

        return (
          <div className="space-y-1 text-left">
            {guestName ? (
              <div className="text-sm font-medium text-zinc-900">
                {guestName}
              </div>
            ) : null}
            {guestSummary?.email ? (
              <div className="text-xs text-zinc-500">{guestSummary.email}</div>
            ) : null}
            {guestId ? (
              <div className="text-xs text-zinc-400">
                {guestId.slice(0, 8)}…
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "bookingId",
      header: t("billing.booking"),
      className: "text-center",
      sortable: true,
      render: (_, row) =>
        row.bookingId ? (
          <span className="text-sm">{row.bookingId.slice(0, 8)}…</span>
        ) : (
          "—"
        ),
    },
    {
      key: "createdAt",
      header: t("billing.date"),
      className: "text-center",
      sortable: true,
      render: (value: string) => (
        <span className="text-sm">
          {format(new Date(value), "dd MMM yyyy HH:mm")}
        </span>
      ),
    },
    {
      key: "stripePaymentIntentId",
      header: t("billing.stripePaymentIntentId"),
      className: "text-center",
      render: (value?: string | null) =>
        value ? <span className="text-xs">{value}</span> : "—",
    },
    ...(canManageBilling
      ? [
          {
            key: "status_action",
            header: t("billing.updateStatus"),
            className: "text-center",
            render: (_: unknown, row: BillingTransaction) => (
              <BillingTransactionStatusSelect
                value={row.status}
                onValueChange={(newStatus) =>
                  handleStatusChange(row.id, newStatus)
                }
              />
            ),
          },
        ]
      : []),
    // {
    //   key: "actions",
    //   header: "Actions",
    //   className: "text-center",
    //   render: (_, row) => (
    //     <div className="flex justify-center">
    //       <Button
    //         variant="ghost"
    //         size="sm"
    //         className="h-8 w-8 p-0"
    //         onClick={() => {
    //           // navigate(`/billing/transactions/${row.id}`)
    //         }}
    //       >
    //         <Eye className="h-4 w-4" />
    //       </Button>
    //     </div>
    //   ),
    // },
  ];

  return (
    <div className="flex flex-col gap-6 p-12">
      {/* Filters and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-end gap-4">
          <BillingTransactionsFilter
            currentHotelId={currentHotelId}
            filters={filters}
            actions={actions}
          />
        </div>
        {canManageBilling ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="new_outline" disabled={isExporting}>
                <Download className="size-4" />
                {isExporting ? t("billing.exporting") : t("billing.export")}
                <ChevronDown className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => void handleExport("excel")}>
                <FileSpreadsheet className="size-4" />
                {t("billing.exportExcel")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => void handleExport("csv")}>
                <FileText className="size-4" />
                {t("billing.exportCsv")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      <BillingTransactionsStats
        isLoading={statsQuery.isLoading}
        stats={statsQuery.data}
      />

      <DataTable
        data={billingTransactions}
        columns={columns}
        onRowClick={handleTransactionClick}
        loading={isLoading}
        pagination={{
          currentPage: pagination.currentPage,
          totalPages: pagination.totalPages,
          onPageChange: pagination.goToPage,
        }}
        sorting={{
          field: filters.sortField,
          direction: filters.sortDirection,
          onSort: actions.handleSort,
        }}
        emptyMessage={t("billing.noBillingTransactionsFound")}
      />

      {error && (
        <div className="py-4 text-center text-red-600">
          {t("billing.errorLoadingTransactions")}: {error.message}
        </div>
      )}
    </div>
  );
};
